import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiButtonEmpty, EuiFlexGroup, EuiFlexItem, EuiHealth, EuiHideFor, EuiImage, EuiInMemoryTable, EuiLoadingSpinner, EuiSpacer, EuiSwitch, EuiText, EuiToolTip, htmlIdGenerator } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'

import { CreativeStatus } from 'api/interfaces'
import config from 'app/config'
import { RootState } from 'app/rootReducer'
import LastCreativePausedErrorModal from 'features/campaigns/campaign/LastCreativePausedErrorModal'
import { resetCreateAd } from 'features/campaigns/createAd/billboards/retail/createAdBillboardRetailSlice'
import history from 'services/HistoryService'
import { CreativeStatusToElasticColor, CreativeStatusToString } from 'utils/EnumToFriendly'

import { DEFAULT_INITIAL_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../utils/DefaultPageSize'
import { AdState, pauseAd, startAd } from '../campaignSlice'

interface AdsDrawerProps {
  CreateAdButtonText?: string
  OnCreateAdClick?: () => void
}

const AdsDrawer = ({ CreateAdButtonText = 'Create Ad', OnCreateAdClick }: AdsDrawerProps) => {
  const dispatch = useDispatch()
  const campaign = useSelector((state: RootState) => state.campaign)
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const [isLastCreativePausedErrorVisible, setIsLastCreativePausedErrorVisible] = useState(false)
  const allowAdCreation = config.features.billboardSuggestionsEnabled
  const [currentPage, setCurrentPage] = useState(0)

  const preview = (thumbnailUrl: string) => (
    <EuiFlexGroup key={htmlIdGenerator()()} responsive={false} style={{ minWidth: 270 }} gutterSize='none'>
      <EuiHideFor sizes={['xs']}>
        <EuiFlexItem grow={false} style={{ width: 150, minWidth: 150 }} className='eui-alignMiddle'>
          <EuiImage alt='ad' url={thumbnailUrl || '/200x82.png'} size='fullWidth' />
        </EuiFlexItem>
      </EuiHideFor>
    </EuiFlexGroup>
  )

  const columns: Array<EuiBasicTableColumn<AdState>> = [
    {
      width: '90px',
      name: 'ON/OFF',
      render: (ad: AdState) => (
        <React.Fragment>
          <EuiSwitch
            showLabel={false}
            label={''}
            checked={ad.checked}
            disabled={ad.disabled}
            onChange={e => {
              if (e.target.checked) {
                dispatch(startAd(currentAccount!.id, campaign!.campaign!.id, ad.id))
              } else {
                if (campaign!.adStates.filter(a => a.checked).length <= 1) {
                  setIsLastCreativePausedErrorVisible(true)
                  return
                }

                dispatch(pauseAd(currentAccount!.id, campaign!.campaign!.id, ad.id))
              }
            }}
          />
          {ad.loading && <EuiLoadingSpinner style={{ marginLeft: 5 }} />}
        </React.Fragment>
      )
    },
    {
      width: '240px',
      name: 'Ad',
      render: (ad: AdState) => preview(ad.thumbnailUrl)
    },
    {
      width: '240px',
      name: 'Name',
      render: (ad: AdState) => ad.name
    },
    {
      width: '110px',
      name: 'Status',
      render: (creative: AdState) => {
        let color = CreativeStatusToElasticColor(creative.status)
        let label = CreativeStatusToString(creative.status)

        return creative.status === CreativeStatus.Disapproved ? (
          <EuiToolTip
            key={htmlIdGenerator()()}
            position='right'
            content={
              <EuiText>
                <ul>
                  {creative.rejectionReasons.map(a => (
                    <li>{a}</li>
                  ))}
                </ul>
              </EuiText>
            }>
            <EuiHealth color={color}>{label}</EuiHealth>
          </EuiToolTip>
        ) : (
          <EuiHealth key={htmlIdGenerator()()} color={color}>
            {label}
          </EuiHealth>
        )
      }
    },
    {
      width: '215px',
      name: '',
      render: (ad: AdState) => (
        <React.Fragment key={htmlIdGenerator()()}>
          <EuiButtonEmpty id='view' iconType='inspect' onClick={() => history.push(`/campaigns/ads/${campaign.campaign!.id}/edit/${ad.id}`)} size='s'>
            View
          </EuiButtonEmpty>
        </React.Fragment>
      )
    }
  ]

  const pagination = {
    initialPageSize: DEFAULT_INITIAL_PAGE_SIZE,
    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
    pageIndex: currentPage
  }

  const createAd = () => {
    dispatch(resetCreateAd())
    OnCreateAdClick?.()
  }

  return (
    <React.Fragment>
      <EuiSpacer />
      {allowAdCreation && (
        <EuiFlexGroup alignItems='center'>
          <EuiFlexItem />
          <EuiFlexItem grow={false}>
            <EuiButton id='create' size='s' key='createAd' onClick={createAd} fill>
              {CreateAdButtonText}
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      )}
      <EuiInMemoryTable
        loading={campaign.isLoadingAds}
        items={campaign.adStates}
        columns={columns}
        pagination={pagination}
        responsive={false}
        onChange={(criteria: any) => {
          setCurrentPage(criteria.page.index)
        }}
      />
      <LastCreativePausedErrorModal isModalVisible={isLastCreativePausedErrorVisible} closeModal={() => setIsLastCreativePausedErrorVisible(false)} />
    </React.Fragment>
  )
}

export default AdsDrawer
